import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, paxRoute, StreamResponse } from "../../common";
import { GameSpansQuery, GameSpansResponse } from "./schemas";

export const service = {
  group: "traces",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Traces API",
  description:
    "Everything recorded for one game: its deltas and spans. Readable by the game's owner and by developers.",
} as const satisfies ContractService;

function endpoint(name: string, path: `/${string}`, summary: string, stream = false) {
  return oc
    .meta(
      paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
      openapi({
        method: "GET",
        path,
        operationId: `traces.games.${name}`,
        summary,
        inputStructure: "detailed",
        ...(stream ? { outputStructure: "detailed" as const } : {}),
      }),
    )
    .errors({ ...errors.unauthorized, ...errors.forbidden, ...errors.notFound });
}

const root = "/api/traces/v1alpha1/games/{gameId}";
const gameParams = z.object({ gameId: z.uuid() });

export const listDeltas = endpoint(
  "deltas.list",
  `${root}/deltas`,
  "Download a game's committed deltas as NDJSON, or only those committed after a revision.",
  true,
)
  .input(
    z.object({
      params: gameParams,
      query: z.object({
        after: z.coerce
          .number()
          .int()
          .nonnegative()
          .default(0)
          .meta({ description: "Only deltas after this revision; the whole record when 0." }),
      }),
    }),
  )
  .output(StreamResponse);

export const listSpans = endpoint(
  "spans.list",
  `${root}/spans`,
  "List the spans recorded for a game, newest first, one page at a time.",
)
  .input(z.object({ params: gameParams, query: GameSpansQuery }))
  .output(GameSpansResponse);

export const getSpan = endpoint(
  "spans.get",
  `${root}/spans/{spanId}`,
  "Read one span exactly as it is stored, with its resource, scope, events and links.",
)
  .input(z.object({ params: gameParams.extend({ spanId: z.string().regex(/^[0-9a-f]{16}$/) }) }))
  .output(z.object({ row: z.record(z.string(), z.unknown()) }));
